import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Users, DollarSign, Gamepad2, TrendingUp, TrendingDown, Clock, Building2, ArrowUpCircle, ArrowDownCircle, Zap, Wallet, BarChart3, PieChart, Activity, CreditCard, Gift, RefreshCw, Download } from 'lucide-react';
import { fmtFP, fmtUSD } from '../../lib/money';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [gameStats, setGameStats] = useState(null);
  const [walletSummary, setWalletSummary] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, revRes, usrRes, gmeRes, walRes, syncRes] = await Promise.all([
        api.get('/api/admin/dashboard').catch(() => ({ data: {} })),
        api.get('/api/admin/analytics/revenue?days=30').catch(() => ({ data: {} })),
        api.get('/api/admin/analytics/users?days=30').catch(() => ({ data: {} })),
        api.get('/api/admin/analytics/games?days=30').catch(() => ({ data: {} })),
        api.get('/api/admin/analytics/wallets/summary').catch(() => ({ data: {} })),
        api.get('/api/admin/sync/status').catch(() => ({ data: {} })),
      ]);
      const s = dashRes.data.stats || {};
      setStats({
        total_players: s.users?.total || 0,
        new_24h: s.users?.new_24h || 0,
        pending_deposits: s.deposits?.pending || 0,
        pending_withdrawals: s.withdrawals?.pending || 0,
        total_deposit_amount: s.deposits?.total_amount || 0,
        total_withdrawal_amount: s.withdrawals?.total_amount || 0,
        active_games: s.games?.active || 0,
        live_activity: dashRes.data.live_activity || [],
        pending_approvals: dashRes.data.pending_approvals || {},
      });
      setRevenue(revRes.data);
      setUserStats(usrRes.data);
      setGameStats(gmeRes.data);
      setWalletSummary(walRes.data);
      setSyncStatus(syncRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try { await api.post('/api/admin/sync/trigger'); } catch {}
    finally { setTimeout(() => setSyncing(false), 3000); }
  };

  const exportCSV = (type) => {
    const token = localStorage.getItem('session_token');
    window.open(`${BACKEND_URL}/api/admin/export/${type}?token=${token}`, '_blank');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const netRev = (revenue?.net_revenue || 0);
  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Dashboard</h2>
          <p className="text-sm text-gray-400">Welcome back, {user?.username || user?.email?.split('@')[0]}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative group">
            <button className="p-2 rounded-lg hover:bg-white/5 text-gray-400 transition" data-testid="export-menu-btn">
              <Download className="w-5 h-5" />
            </button>
            <div className="absolute right-0 top-10 w-48 bg-[#1a1f2e] border border-white/10 rounded-xl shadow-xl hidden group-hover:block z-50">
              <button onClick={() => exportCSV('players')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 rounded-t-xl" data-testid="export-players">Export Players CSV</button>
              <button onClick={() => exportCSV('transactions')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5" data-testid="export-transactions">Export Transactions CSV</button>
              <button onClick={() => exportCSV('game-transactions')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 rounded-b-xl" data-testid="export-game-txns">Export Game Credits CSV</button>
            </div>
          </div>
          <button onClick={fetchAll} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 transition" data-testid="refresh-dashboard">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users} label="Total Players" value={stats?.total_players || 0} sub={`+${stats?.new_24h || 0} today`} color="#3B82F6" />
        <KPI icon={ArrowDownCircle} label="Pending Deposits" value={stats?.pending_deposits || 0} color="#10B981" urgent={stats?.pending_deposits > 0} />
        <KPI icon={ArrowUpCircle} label="Pending Withdrawals" value={stats?.pending_withdrawals || 0} color="#EF4444" urgent={stats?.pending_withdrawals > 0} />
        <KPI icon={Gamepad2} label="Active Games" value={stats?.active_games || 0} color="#D4AF37" />
      </div>

      {/* Revenue Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#D4AF37]" /> Revenue Overview (30 days)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MiniStat label="Total Deposits" value={fmtUSD(revenue?.total_deposits)} sub={`${revenue?.deposit_count || 0} txns`} color="#22c55e" />
            <MiniStat label="Total Withdrawals" value={fmtUSD(revenue?.total_withdrawals)} sub={`${revenue?.withdrawal_count || 0} txns`} color="#ef4444" />
            <MiniStat label="Fees Collected" value={fmtUSD(revenue?.total_fees)} color="#D4AF37" />
            <MiniStat label="Net Revenue" value={fmtUSD(netRev)} color={netRev >= 0 ? '#22c55e' : '#ef4444'} trend={netRev >= 0 ? 'up' : 'down'} />
          </div>
          {/* Visual bar for deposits vs withdrawals */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
              <span>Deposits vs Withdrawals</span>
              <span>{fmtUSD(revenue?.total_deposits)} / {fmtUSD(revenue?.total_withdrawals)}</span>
            </div>
            <div className="h-3 rounded-full bg-white/5 overflow-hidden flex">
              {(() => {
                const total = (revenue?.total_deposits || 0) + (revenue?.total_withdrawals || 0);
                const depPct = total > 0 ? ((revenue?.total_deposits || 0) / total * 100) : 50;
                return (
                  <>
                    <div className="h-full rounded-l-full" style={{ width: `${depPct}%`, background: 'linear-gradient(90deg, #22c55e, #16a34a)' }} />
                    <div className="h-full rounded-r-full" style={{ width: `${100 - depPct}%`, background: 'linear-gradient(90deg, #ef4444, #dc2626)' }} />
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Wallet Summary */}
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Wallet className="w-4 h-4 text-[#D4AF37]" /> Platform Wallets</h3>
          <div className="space-y-3">
            <WalletRow label="Total Cash" value={fmtUSD(walletSummary?.total_cash)} color="#22c55e" />
            <WalletRow label="FP Credits" value={fmtFP(walletSummary?.total_play_credits)} color="#3B82F6" />
            <WalletRow label="Pending" value={fmtUSD(walletSummary?.total_pending)} color="#eab308" />
            <WalletRow label="Locked Commissions" value={fmtUSD(walletSummary?.total_locked)} color="#f97316" />
            <div className="pt-2 border-t border-white/5">
              <WalletRow label="Cash + FP" value={`${fmtUSD(walletSummary?.total_cash)} + ${fmtFP(walletSummary?.total_play_credits)}`} color="#D4AF37" bold />
            </div>
          </div>
        </div>
      </div>

      {/* User & Game Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* User Analytics */}
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-[#3B82F6]" /> User Analytics (30 days)</h3>
          <div className="grid grid-cols-2 gap-3">
            <MiniCard label="New Users" value={userStats?.new_users || 0} icon={Users} color="#3B82F6" />
            <MiniCard label="Active Sessions" value={userStats?.active_users || 0} icon={Activity} color="#22c55e" />
            <MiniCard label="Growth Rate" value={`${userStats?.growth_rate || 0}%`} icon={TrendingUp} color="#D4AF37" />
            <MiniCard label="Disabled" value={userStats?.disabled_users || 0} icon={Users} color="#ef4444" />
          </div>
        </div>

        {/* Game Analytics */}
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Gamepad2 className="w-4 h-4 text-[#EC4899]" /> Game Activity (30 days)</h3>
          <div className="grid grid-cols-2 gap-3">
            <MiniCard label="Total Loads" value={gameStats?.total_loads || 0} icon={ArrowDownCircle} color="#6C5CE7" />
            <MiniCard label="Load Volume" value={fmtUSD(gameStats?.load_volume)} icon={DollarSign} color="#6C5CE7" />
            <MiniCard label="Total Redeems" value={gameStats?.total_redeems || 0} icon={ArrowUpCircle} color="#22c55e" />
            <MiniCard label="Redeem Volume" value={fmtUSD(gameStats?.redeem_volume)} icon={DollarSign} color="#22c55e" />
          </div>
          {/* By Provider */}
          {gameStats?.by_provider?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/5">
              <p className="text-[10px] text-gray-500 mb-2 font-medium">BY PROVIDER</p>
              <div className="space-y-1.5">
                {gameStats.by_provider.slice(0, 5).map(p => (
                  <div key={p.provider} className="flex items-center justify-between text-xs">
                    <span className="text-gray-400 capitalize">{p.provider}</span>
                    <div className="flex gap-3">
                      <span className="text-gray-300">{p.count} txns</span>
                      <span className="text-[#D4AF37] font-medium">{fmtUSD(p.volume)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Live Activity */}
      <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
        <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-[#D4AF37]" /> Live Activity (24h)</h3>
        {(stats?.live_activity || []).length === 0 ? (
          <p className="text-center text-gray-500 py-6 text-sm">No recent activity</p>
        ) : (
          <div className="space-y-2">
            {(stats?.live_activity || []).slice(0, 10).map((act, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[#0B0E14]">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: act.type === 'deposit' ? 'rgba(34,197,94,0.15)' : act.type === 'payout' ? 'rgba(239,68,68,0.15)' : 'rgba(212,175,55,0.15)' }}>
                    {act.type === 'deposit' ? <ArrowDownCircle className="w-4 h-4 text-green-400" /> :
                     act.type === 'payout' ? <ArrowUpCircle className="w-4 h-4 text-red-400" /> :
                     <Activity className="w-4 h-4 text-[#D4AF37]" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{act.username || 'Unknown'}</p>
                    <p className="text-xs text-gray-500 capitalize">{act.type} {act.action && `- ${act.action}`}</p>
                  </div>
                </div>
                <div className="text-right">
                  {act.amount && <p className="text-sm font-semibold" style={{ color: act.type === 'deposit' ? '#22c55e' : '#ef4444' }}>{fmtUSD(act.amount)}</p>}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    act.status === 'APPROVED' || act.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                    act.status === 'UNDER_REVIEW' || act.status === 'PENDING_REVIEW' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>{act.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sync Status */}
      {syncStatus?.last_sync && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5" data-testid="sync-status-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><RefreshCw className="w-4 h-4 text-[#D4AF37]" /> Balance Sync</h3>
            <button onClick={triggerSync} disabled={syncing} className="text-xs px-3 py-1.5 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition disabled:opacity-50" data-testid="trigger-sync-btn">
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-[#0B0E14] rounded-lg p-2.5 text-center">
              <p className="text-sm font-bold text-green-400">{syncStatus.last_sync?.synced || 0}</p>
              <p className="text-[9px] text-gray-500">Synced</p>
            </div>
            <div className="bg-[#0B0E14] rounded-lg p-2.5 text-center">
              <p className="text-sm font-bold text-red-400">{syncStatus.last_sync?.errors || 0}</p>
              <p className="text-[9px] text-gray-500">Errors</p>
            </div>
            <div className="bg-[#0B0E14] rounded-lg p-2.5 text-center">
              <p className="text-sm font-bold text-blue-400">{syncStatus.last_sync?.total || 0}</p>
              <p className="text-[9px] text-gray-500">Total</p>
            </div>
            <div className="bg-[#0B0E14] rounded-lg p-2.5 text-center">
              <p className="text-sm font-bold text-[#D4AF37]">{syncStatus.last_sync?.value ? new Date(syncStatus.last_sync.value).toLocaleTimeString() : 'Never'}</p>
              <p className="text-[9px] text-gray-500">Last Sync</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color, urgent }) {
  return (
    <div className={`bg-[#131822] rounded-2xl border p-4 ${urgent ? 'border-yellow-500/30' : 'border-white/10'}`}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-[11px] text-gray-500">{label}</p>
          <p className="text-xl font-bold text-white">{value}</p>
          {sub && <p className="text-[10px] text-gray-500">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, color, trend }) {
  return (
    <div>
      <p className="text-[10px] text-gray-500 mb-1">{label}</p>
      <div className="flex items-center gap-1">
        <p className="text-lg font-bold" style={{ color }}>{value}</p>
        {trend === 'up' && <TrendingUp className="w-3.5 h-3.5 text-green-400" />}
        {trend === 'down' && <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
      </div>
      {sub && <p className="text-[10px] text-gray-500">{sub}</p>}
    </div>
  );
}

function WalletRow({ label, value, color, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-xs ${bold ? 'text-white font-semibold' : 'text-gray-400'}`}>{label}</span>
      <span className={`text-sm font-mono ${bold ? 'font-bold' : 'font-medium'}`} style={{ color }}>{value}</span>
    </div>
  );
}

function MiniCard({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-xl p-3" style={{ background: `${color}08`, border: `1px solid ${color}15` }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <p className="text-[10px] text-gray-500">{label}</p>
      </div>
      <p className="text-base font-bold" style={{ color }}>{value}</p>
    </div>
  );
}
